import { useMemo, useState } from "react";
import {
  campaignsApi,
  useAnalyzeGeoTargetingMutation,
  useUpdateTargetingConfigurationMutation,
} from "../../../../api";
import { ReuseModal } from "../../ReuseModal";
import { Stack } from "@mui/material";
import {
  AccountSize,
  DropdownItem,
  GetCampaignFieldsResponse,
} from "../../../../types/apiTypes";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { AiAssistantBlock } from "../../../products/CreateCampaignFlow/AIAssistantBlock";
import { LabelWrapper } from "../../../products/CreateCampaignFlow/steps/AddPreferencesStep/LabelWrapper";
import { NestedDropdown } from "../../../products/CreateCampaignFlow/steps/AddPreferencesStep/NestedDropdown";
import { SizesDropdown } from "../../../products/CreateCampaignFlow/steps/AddPreferencesStep/SizesDropdown";
import { InputDropdown } from "../../../products/CreateCampaignFlow/steps/AddPreferencesStep/InputDropdown";
import {
  toDropdownIndustries,
  toDropdownRegions,
} from "../../../../utils/convertToDropdownNesteItem";
import { DropdownNestedItem } from "../../../../types/genereal";
import { TargetingConfiguration } from "../../../../types";
import { ExcludeAccountsBlock } from "./ExcludeAccountsBlock";
import { useAppDispatch } from "../../../../hooks";

type Props = {
  campaign_id: string;
  onClose: () => void;
  currentTargeting: TargetingConfiguration;
  campaignFieldsData: GetCampaignFieldsResponse;
};

export const CustomizeTargetingModal = ({
  campaign_id,
  currentTargeting,
  campaignFieldsData,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const [analyzeGeoTargeting, { isLoading }] = useAnalyzeGeoTargetingMutation();
  const [updateTargetingConfiguration, { isLoading: isUpdating }] =
    useUpdateTargetingConfigurationMutation();

  const [isResetIgnore, setIsResetIgnore] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const {
    target_industries,
    target_regions,
    target_account_sizes,
    target_revenue,
    ignored_accounts_file_names,
  } = useMemo(() => {
    const target_regions = (currentTargeting.target_regions || []).map(
      (el) => ({
        ...el,
        countries: el.countries.map((name) => ({ name, isAdded: false })),
        isAdded: false,
      }),
    );
    const target_industries = (currentTargeting.target_industries || []).map(
      (el) => ({
        ...el,
        sub_industries: el.sub_industries.map((name) => ({
          name,
          isAdded: false,
        })),
        isAdded: false,
      }),
    );

    const target_account_sizes = (
      campaignFieldsData?.account_sizes || []
    ).filter(({ min_max }) =>
      (currentTargeting.target_account_size || []).includes(min_max),
    );

    const target_revenue = (currentTargeting.target_revenue || []).map(
      (value) => ({
        id: value,
        value,
        isAdded: false,
      }),
    );

    return {
      target_industries,
      target_regions,
      target_account_sizes,
      target_revenue,
      ignored_accounts_file_names:
        currentTargeting?.ignored_accounts_file_names || [],
    };
  }, [currentTargeting]);

  const initialIndustries = toDropdownIndustries(target_industries);
  const [selectedIndustries, setSelectedIndustries] =
    useState<DropdownNestedItem[]>(initialIndustries);

  const initialRegions = toDropdownRegions(target_regions);
  const [selectedRegions, setSelectedRegions] =
    useState<DropdownNestedItem[]>(initialRegions);

  const [selectedSizes, setSelectedSizes] =
    useState<AccountSize[]>(target_account_sizes);
  const [selectedRevenues, setSelectedRevenues] =
    useState<DropdownItem[]>(target_revenue);

  const campaignFields = campaignFieldsData || {
    account_sizes: [],
    regions: [],
    industries: [],
    revenues: [],
  };

  const preparIndustries = useMemo(
    () =>
      campaignFields.industries.map((el) => ({
        ...el,
        sub_industries: el.sub_industries.map((name) => ({
          name,
          isAdded: false,
        })),
      })),
    [campaignFields],
  );

  const preparRegions = useMemo(
    () =>
      campaignFields.regions.map((el) => ({
        ...el,
        countries: el.countries.map((name) => ({ name, isAdded: false })),
      })),
    [campaignFields],
  );

  const preparRevenues = useMemo(
    () =>
      campaignFields.revenues.map((value) => ({
        value,
        isAdded: false,
        id: value,
      })),
    [campaignFields],
  );

  const industries = toDropdownIndustries(preparIndustries);
  const regions = toDropdownRegions(preparRegions);

  const addAssistantData = async (text: string) => {
    await analyzeGeoTargeting({ text })
      .unwrap()
      .then(({ result }) => {
        const target_regions: DropdownNestedItem[] = result.target_regions.map(
          ({ name, countries }) => ({
            name,
            nestedItems: countries.map((country) => ({
              name: country,
              isAdded: true,
              parent: name,
            })),
            selected: !countries.length,
            isAdded: true,
          }),
        );

        const target_industries: DropdownNestedItem[] =
          result.target_industries.map(({ name, sub_industries }) => ({
            name,
            nestedItems: sub_industries.map((sub) => ({
              name: sub,
              isAdded: true,
              parent: name,
            })),
            selected: !sub_industries.length,
            isAdded: true,
          }));

        const mergeData = (
          org: DropdownNestedItem[],
          added: DropdownNestedItem[],
        ) => {
          const oldUnic = org.filter(
            ({ name }) => !added.some((el) => el.name === name),
          );

          const merged = added.map((el) => {
            const inList = org.find((origin) => origin.name === el.name);
            if (inList) {
              if (inList.selected) return inList;

              const mergedSum = [...inList.nestedItems];

              el.nestedItems.forEach((nestedItem) => {
                if (
                  !inList.nestedItems.some(
                    ({ name }) => name === nestedItem.name,
                  )
                )
                  mergedSum.push(nestedItem);
              });

              return {
                name: el.name,
                selected: !el.nestedItems.length,
                isAdded: true,
                nestedItems: !el.nestedItems.length ? [] : mergedSum,
              };
            }

            return el;
          });

          return [...oldUnic, ...merged];
        };

        setSelectedIndustries((prev) => mergeData(prev, target_industries));
        setSelectedRegions((prev) => mergeData(prev, target_regions));

        setSelectedSizes((prev) => {
          const target_account_sizes: AccountSize[] = [];
          result.target_account_size.forEach((el) => {
            if (prev.some((orig) => orig.min_max === el.min_max)) return;
            target_account_sizes.push({ ...el, isAdded: true });
          });

          return [...prev, ...target_account_sizes];
        });

        setSelectedRevenues((prev) => {
          const revenues: DropdownItem[] = [];
          result.target_revenue.forEach((value) => {
            if (prev.some((orig) => orig.value === value)) return;
            revenues.push({ value, isAdded: true, id: value });
          });

          return [...prev, ...revenues];
        });
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={getErrorMessage(err).globalError || "Failed to analyzed data"}
            title="Error"
          />,
        ),
      );
  };

  const onFileUpload = (files: File[]) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFileHandler = (fileName: string) => {
    if (isLoading) return;
    setUploadedFiles((state) => state.filter(({ name }) => name !== fileName));
  };

  const changeFileHandler = (fileName: string, file: File) => {
    if (isLoading) return;
    setUploadedFiles((state) =>
      state.map((el) => {
        if (el.name !== fileName) return el;
        return file;
      }),
    );
  };

  const geo_list = useMemo(
    () =>
      selectedRegions.map(({ name, nestedItems, selected }) => ({
        name,
        countries: nestedItems.map(({ name }) => name),
        is_all_countries: !!selected,
      })),
    [selectedRegions],
  );

  const industry_list = useMemo(
    () =>
      selectedIndustries.map(({ name, nestedItems, selected }) => ({
        name,
        sub_industries: nestedItems.map(({ name }) => name),
        is_all_sub_industries: !!selected,
      })),
    [selectedIndustries],
  );

  const revenue_list = useMemo(
    () => selectedRevenues.map(({ value }) => value),
    [selectedRevenues],
  );

  const size_list = useMemo(
    () => selectedSizes.map(({ min_max }) => min_max),
    [selectedSizes],
  );

  const checkSame = (list: Object[][]) => {
    return list.some(
      ([el, compareEl]) => JSON.stringify(el) !== JSON.stringify(compareEl),
    );
  };

  const isAllowedSubmit =
    uploadedFiles.length > 0 ||
    isResetIgnore ||
    checkSame([
      [currentTargeting.target_regions, geo_list],
      [currentTargeting.target_industries, industry_list],
      [currentTargeting.target_account_size, size_list],
      [currentTargeting.target_revenue, revenue_list],
    ]);

  const summitHandler = async () => {
    updateTargetingConfiguration({
      campaign_id,
      files: uploadedFiles,
      geo_list,
      industry_list,
      revenue_list,
      size_list,
      reset_ignored: isResetIgnore,
    })
      .unwrap()
      .then(() => {
        dispatch(campaignsApi.util.invalidateTags(["summary"]));
        onClose();
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Failed Customize Targeting"}
          />,
        ),
      );
  };

  return (
    <ReuseModal
      isOpen
      onClose={onClose}
      onSubmit={summitHandler}
      title="Customize Targeting"
      maxWidth="1114px"
      submitBtnText={"Save"}
      submitBtnColor="#1C3C6C"
      isDisabled={isLoading || isUpdating || !isAllowedSubmit}
      isDisabledCancelBtn={isUpdating}
      isLoading={isUpdating}
      sxContentWrapper={{ paddingLeft: "4px" }}
    >
      <Stack
        flexDirection={"row"}
        gap={"32px"}
        paddingBottom={"8px"}
        alignItems={"flex-start"}
      >
        <Stack direction="column" gap="24px" width="100%">
          <AiAssistantBlock
            isLoading={isLoading}
            submitHandler={addAssistantData}
            title={"Tell Us More About Your Targeting"}
            subtitle={
              "Our AI assistant will analyze your written input to extract and" +
              " structure target locations, industries, company size and revenue range."
            }
            placeholder={
              "E.g. \n" +
              "Geo: \n" +
              "US, Canada, EMEA\n" +
              "Industry: \n" +
              "Finance, Retail, Energy: Oil & Gas\n" +
              "Size: \n" +
              "1000+\n" +
              "Revenue: \n" +
              "1M+"
            }
          />
          <LabelWrapper
            title="Geo"
            label="Select the region or specific countries where your target accounts are located."
          >
            <NestedDropdown
              items={regions}
              selected={selectedRegions}
              setSelected={setSelectedRegions}
              placeholder="Search Region or Countries"
              selectAllText="Whole region"
            />
          </LabelWrapper>

          <LabelWrapper
            title="Industry"
            label="Search industries or sub-industries that match your ideal customer profile."
          >
            <NestedDropdown
              items={industries}
              selected={selectedIndustries}
              setSelected={setSelectedIndustries}
              placeholder="Search Industry or Sub-Industry"
              selectAllText="Whole industry"
            />
          </LabelWrapper>

          <LabelWrapper
            title="Size"
            label="Search the company size by employees count that best fits your target accounts."
          >
            <SizesDropdown
              items={campaignFields.account_sizes}
              selected={selectedSizes}
              setSelected={setSelectedSizes}
              placeholder="Search Company Size"
            />
          </LabelWrapper>

          <LabelWrapper
            title="Revenue"
            label="Search the company revenue range that best fits your target accounts"
          >
            <InputDropdown
              items={preparRevenues}
              selected={selectedRevenues}
              setSelected={setSelectedRevenues}
              placeholder="Search Company Revenu"
            />
          </LabelWrapper>
        </Stack>
        <ExcludeAccountsBlock
          ignoredAccounts={ignored_accounts_file_names}
          isResetIgnore={isResetIgnore}
          setIsResetIgnore={() => setIsResetIgnore(true)}
          uploadedFiles={uploadedFiles}
          isLoading={isLoading}
          onFileUpload={onFileUpload}
          removeFileHandler={removeFileHandler}
          changeFileHandler={changeFileHandler}
        />
      </Stack>
    </ReuseModal>
  );
};
